/**
 * Shared REST client for api.kei.bio.
 *
 * Mirrors the existing kei.bio frontend's `src/apiClient.js` contract on purpose:
 * same base URL fallback, same `credentials: 'include'` (so the `.kei.bio` domain
 * `kei_token` session cookie is shared across kei.bio and meet.kei.bio without a
 * separate login), and the same `kei:unauthorized` window event on 401 so a shared
 * "session expired" handler could eventually be reused. MEET never stores its own
 * copy of auth secrets — it only rides the existing cookie.
 */

const DEFAULT_BASE = (import.meta.env.VITE_API_URL as string | undefined) || 'https://api.kei.bio'

export class ApiError extends Error {
  status: number
  body: unknown

  constructor(status: number, message: string, body: unknown) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.body = body
  }
}

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown
  base?: string
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, base = DEFAULT_BASE, headers, ...rest } = options

  const res = await fetch(`${base}${path}`, {
    ...rest,
    credentials: 'include',
    headers: {
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...headers
    },
    body: body !== undefined ? JSON.stringify(body) : undefined
  })

  if (res.status === 401) {
    window.dispatchEvent(new CustomEvent('kei:unauthorized'))
  }

  const contentType = res.headers.get('content-type') ?? ''
  const payload = contentType.includes('application/json') ? await res.json().catch(() => null) : null

  if (!res.ok) {
    throw new ApiError(res.status, (payload as { message?: string } | null)?.message ?? res.statusText, payload)
  }

  return payload as T
}

/**
 * 2026-09-14 — "에러 메세지 만들지 말고요, 서버가 꺼져 있으면 '현재 미팅룸
 * 운영시간이 아닙니다' 메세지 보여지도록 해주세요" (직접 지시). Room 자체의
 * 실제 상태(꽉 참/종료됨/없음)는 구체적으로 알려주고, 그 외 모든 인프라 수준
 * 실패(서버 다운, 네트워크 오류, 예상 못한 5xx)는 겁주는 기술 에러 대신 이
 * 한 문장으로 통일한다. 401은 로그인 유도 UI가 따로 있는 화면(NewMeeting 등)
 * 에서 별도 처리하고 여기서는 안 건드린다.
 *
 * i18n(2026-09-14): 이 함수는 컴포넌트 setup 바깥(비동기 catch 블록)에서도
 * 호출되므로 useI18n()을 여기서 직접 쓸 수 없다 — 호출부(컴포넌트/컴포저블)의
 * `t`를 그대로 전달받아 번역한다.
 */
type Translate = (key: string) => string

export function friendlyMeetError(e: unknown, t: Translate): string {
  if (e instanceof ApiError) {
    const code = (e.body as { error?: string } | null)?.error
    if (code === 'ROOM_NOT_FOUND') return t('errors.roomNotFound')
    if (code === 'ROOM_ENDED') return t('errors.roomEnded')
    if (code === 'ROOM_FULL') return t('errors.roomFull')
  }
  return t('errors.meetServiceUnavailable')
}

export const api = {
  get: <T>(path: string, options?: RequestOptions) => request<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'POST', body }),
  patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'PUT', body }),
  delete: <T>(path: string, options?: RequestOptions) => request<T>(path, { ...options, method: 'DELETE' })
}
